/**
 * Reviewed reaction-prediction data and formula helpers: solubility rules, activity series,
 * reactant catalogues and exact reviewed cases. The prediction logic lives in reactionPrediction.ts.
 */

export type ReactionFamily =
  | 'precipitation'
  | 'displacement'
  | 'metal-acid'
  | 'acid-carbonate'
  | 'acid-base'
  | 'combustion'
  | 'synthesis'
  | 'decomposition';

export type ReactionOutcome = 'reaction' | 'no-reaction';

export type ObservableEvidence =
  | 'precipitate'
  | 'metal-deposit'
  | 'gas-h2'
  | 'gas-co2'
  | 'gas-o2'
  | 'heat-light'
  | 'warming'
  | 'no-change';

export type Solubility = 'soluble' | 'insoluble' | 'slightly-soluble';

export interface ReactionDataProvenance {
  sources: string[];
  conditions: string;
  reviewStatus: 'pending-teaching-review';
}

export const REACTION_DATA_PROVENANCE: ReactionDataProvenance = {
  sources: [
    'Standard senior chemistry solubility rules for common ionic compounds in water (as tabulated in common Australian senior chemistry texts; values recorded, no exam data-sheet layout copied)',
    'Metal reactivity (activity) series K > Na > Ca > Mg > Al > Zn > Fe > Sn > Pb > H > Cu > Ag > Au, consistent with IUPAC standard electrode potentials (CRC Handbook of Chemistry and Physics)',
    'Precipitate colours from standard qualitative-analysis references',
  ],
  conditions:
    'Dilute aqueous solutions at about 25 °C and 100 kPa. Combustion assumes plenty of oxygen (complete combustion).',
  reviewStatus: 'pending-teaching-review',
};

// ---------------------------------------------------------------------------
// Formula helpers
// ---------------------------------------------------------------------------

export const SUBSCRIPT_DIGITS = '₀₁₂₃₄₅₆₇₈₉';
export const SUPERSCRIPT_DIGITS = '⁰¹²³⁴⁵⁶⁷⁸⁹';

/** Converts an ASCII formula such as `Ca3(PO4)2` into `Ca₃(PO₄)₂`. */
export function formatFormula(ascii: string): string {
  return ascii.replace(/([A-Za-z)])(\d+)/g, (_, before: string, digits: string) =>
    before + [...digits].map((digit) => SUBSCRIPT_DIGITS[Number(digit)]).join(''),
  );
}

/** Formats a species id such as `AgNO3(aq)` for display. */
export function formatSpecies(id: string): string {
  const match = /^(.*?)(\((?:s|l|g|aq)\))$/.exec(id);
  return match ? `${formatFormula(match[1])}${match[2]}` : formatFormula(id);
}

export function formatCharge(charge: number): string {
  const magnitude = Math.abs(charge);
  const digits = magnitude === 1 ? '' : [...String(magnitude)].map((d) => SUPERSCRIPT_DIGITS[Number(d)]).join('');
  return `${digits}${charge > 0 ? '⁺' : '⁻'}`;
}

/** Counts atoms of each element in an ASCII formula (supports one level of nested brackets). */
export function parseFormula(ascii: string): Record<string, number> {
  const counts: Record<string, number> = {};
  const add = (element: string, count: number) => {
    counts[element] = (counts[element] ?? 0) + count;
  };
  const tokens = ascii.match(/[A-Z][a-z]?|\d+|\(|\)/g);
  if (!tokens || tokens.join('') !== ascii) throw new Error(`Unsupported formula: ${ascii}`);
  const stack: Record<string, number>[] = [];
  let current: Record<string, number> = {};
  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index];
    const next = tokens[index + 1];
    const multiplier = next && /^\d+$/.test(next) ? Number(next) : 1;
    if (token === '(') {
      stack.push(current);
      current = {};
    } else if (token === ')') {
      const group = current;
      current = stack.pop() ?? {};
      Object.entries(group).forEach(([element, count]) => {
        current[element] = (current[element] ?? 0) + count * multiplier;
      });
      if (multiplier !== 1) index += 1;
    } else if (/^[A-Z]/.test(token)) {
      current[token] = (current[token] ?? 0) + multiplier;
      if (multiplier !== 1) index += 1;
    }
  }
  if (stack.length > 0) throw new Error(`Unbalanced brackets: ${ascii}`);
  Object.entries(current).forEach(([element, count]) => add(element, count));
  return counts;
}

export function stripState(id: string) {
  return id.replace(/\((?:s|l|g|aq)\)$/, '');
}

export interface EquationTerm {
  coefficient: number;
  species: string;
}

export const MAX_BALANCE_COEFFICIENT = 8;

/**
 * Finds the smallest whole-number coefficients that conserve every element. Brute force is
 * deliberate: the reviewed equations have at most five species and small coefficients.
 */
export function balanceEquation(reactants: string[], products: string[]): EquationTerm[] | null {
  const species = [...reactants, ...products];
  const counts = species.map((id) => parseFormula(stripState(id)));
  const elements = [...new Set(counts.flatMap((count) => Object.keys(count)))];
  const coefficients = new Array<number>(species.length).fill(1);
  const balanced = () =>
    elements.every((element) => {
      let total = 0;
      counts.forEach((count, index) => {
        const sign = index < reactants.length ? 1 : -1;
        total += sign * coefficients[index] * (count[element] ?? 0);
      });
      return total === 0;
    });
  const search = (position: number): boolean => {
    if (position === species.length) return balanced();
    for (let value = 1; value <= MAX_BALANCE_COEFFICIENT; value += 1) {
      coefficients[position] = value;
      if (search(position + 1)) return true;
    }
    return false;
  };
  if (!search(0)) return null;
  return species.map((id, index) => ({ coefficient: coefficients[index], species: id }));
}

export function isBalanced(reactants: EquationTerm[], products: EquationTerm[]) {
  const totals: Record<string, number> = {};
  const addTerms = (terms: EquationTerm[], sign: number) =>
    terms.forEach((term) =>
      Object.entries(parseFormula(stripState(term.species))).forEach(([element, count]) => {
        totals[element] = (totals[element] ?? 0) + sign * term.coefficient * count;
      }),
    );
  addTerms(reactants, 1);
  addTerms(products, -1);
  return Object.values(totals).every((total) => total === 0);
}

export function formatTerms(terms: EquationTerm[]) {
  return terms
    .map((term) => `${term.coefficient === 1 ? '' : term.coefficient}${formatSpecies(term.species)}`)
    .join(' + ');
}

// ---------------------------------------------------------------------------
// Ions, solubility rules and activity series
// ---------------------------------------------------------------------------

export interface IonData {
  id: string;
  formula: string;
  charge: number;
  polyatomic: boolean;
}

const ion = (id: string, formula: string, charge: number, polyatomic = false): IonData => ({
  id,
  formula,
  charge,
  polyatomic,
});

export const IONS: Record<string, IonData> = Object.fromEntries(
  [
    ion('lithium', 'Li', 1),
    ion('sodium', 'Na', 1),
    ion('potassium', 'K', 1),
    ion('ammonium', 'NH4', 1, true),
    ion('silver', 'Ag', 1),
    ion('lead(II)', 'Pb', 2),
    ion('barium', 'Ba', 2),
    ion('calcium', 'Ca', 2),
    ion('strontium', 'Sr', 2),
    ion('magnesium', 'Mg', 2),
    ion('copper(II)', 'Cu', 2),
    ion('iron(II)', 'Fe', 2),
    ion('iron(III)', 'Fe', 3),
    ion('zinc', 'Zn', 2),
    ion('aluminium', 'Al', 3),
    ion('tin(II)', 'Sn', 2),
    ion('mercury(I)', 'Hg2', 2),
    ion('gold(III)', 'Au', 3),
    ion('nitrate', 'NO3', -1, true),
    ion('chloride', 'Cl', -1),
    ion('bromide', 'Br', -1),
    ion('iodide', 'I', -1),
    ion('sulfate', 'SO4', -2, true),
    ion('carbonate', 'CO3', -2, true),
    ion('hydrogencarbonate', 'HCO3', -1, true),
    ion('hydroxide', 'OH', -1, true),
    ion('phosphate', 'PO4', -3, true),
    ion('oxide', 'O', -2),
  ].map((entry) => [entry.id, entry]),
);

export function formatIon(id: string) {
  const data = IONS[id];
  return `${formatFormula(data.formula)}${formatCharge(data.charge)}`;
}

export function gcd(a: number, b: number): number {
  return b === 0 ? Math.abs(a) : gcd(b, a % b);
}

export function compoundFor(cationId: string, anionId: string) {
  const cation = IONS[cationId];
  const anion = IONS[anionId];
  const divisor = gcd(cation.charge, Math.abs(anion.charge));
  const cationCount = Math.abs(anion.charge) / divisor;
  const anionCount = cation.charge / divisor;
  const part = (data: IonData, count: number) =>
    count === 1 ? data.formula : data.polyatomic ? `(${data.formula})${count}` : `${data.formula}${count}`;
  return {
    formula: `${part(cation, cationCount)}${part(anion, anionCount)}`,
    name: `${cationId} ${anionId}`,
    cationCount,
    anionCount,
  };
}

export interface SolubilityRule {
  id: string;
  statement: string;
  cations?: string[];
  anions?: string[];
  general: Solubility;
  exceptions: { cations: string[]; solubility: Solubility }[];
  boundary?: string;
}

export const GROUP_1_AND_AMMONIUM = ['lithium', 'sodium', 'potassium', 'ammonium'];

export const SOLUBILITY_RULES: SolubilityRule[] = [
  {
    id: 'group-1-ammonium',
    statement: 'Compounds of Group 1 ions (Li⁺, Na⁺, K⁺) and ammonium (NH₄⁺) are soluble.',
    cations: GROUP_1_AND_AMMONIUM,
    general: 'soluble',
    exceptions: [],
  },
  {
    id: 'nitrates',
    statement: 'All nitrates (NO₃⁻) are soluble.',
    anions: ['nitrate'],
    general: 'soluble',
    exceptions: [],
  },
  {
    id: 'halides',
    statement:
      'Chlorides, bromides and iodides are soluble, except those of Ag⁺, Pb²⁺ and Hg₂²⁺.',
    anions: ['chloride', 'bromide', 'iodide'],
    general: 'soluble',
    exceptions: [{ cations: ['silver', 'lead(II)', 'mercury(I)'], solubility: 'insoluble' }],
    boundary: 'Lead(II) chloride and bromide dissolve noticeably in hot water.',
  },
  {
    id: 'sulfates',
    statement:
      'Sulfates are soluble, except those of Ba²⁺, Pb²⁺ and Sr²⁺. Ca²⁺ and Ag⁺ sulfates are only slightly soluble.',
    anions: ['sulfate'],
    general: 'soluble',
    exceptions: [
      { cations: ['barium', 'lead(II)', 'strontium'], solubility: 'insoluble' },
      { cations: ['calcium', 'silver'], solubility: 'slightly-soluble' },
    ],
  },
  {
    id: 'carbonates',
    statement: 'Carbonates are insoluble, except those of Group 1 ions and NH₄⁺.',
    anions: ['carbonate'],
    general: 'insoluble',
    exceptions: [],
  },
  {
    id: 'phosphates',
    statement: 'Phosphates are insoluble, except those of Group 1 ions and NH₄⁺.',
    anions: ['phosphate'],
    general: 'insoluble',
    exceptions: [],
  },
  {
    id: 'hydroxides',
    statement:
      'Hydroxides are insoluble, except those of Group 1 ions and NH₄⁺. Ca²⁺, Ba²⁺ and Sr²⁺ hydroxides are slightly soluble.',
    anions: ['hydroxide'],
    general: 'insoluble',
    exceptions: [{ cations: ['calcium', 'barium', 'strontium'], solubility: 'slightly-soluble' }],
  },
];

export function solubilityOf(
  cationId: string,
  anionId: string,
): { solubility: Solubility; rule: SolubilityRule } | null {
  for (const rule of SOLUBILITY_RULES) {
    const applies = rule.cations ? rule.cations.includes(cationId) : rule.anions?.includes(anionId);
    if (!applies) continue;
    const exception = rule.exceptions.find((entry) => entry.cations.includes(cationId));
    return { solubility: exception?.solubility ?? rule.general, rule };
  }
  return null;
}

/** Reviewed appearance of each precipitate the tool may predict. */
export const PRECIPITATES: Record<string, { appearance: string; boundary?: string }> = {
  'silver|chloride': { appearance: 'white' },
  'silver|bromide': { appearance: 'cream' },
  'silver|iodide': { appearance: 'pale yellow' },
  'silver|carbonate': { appearance: 'pale yellow' },
  'silver|phosphate': { appearance: 'yellow' },
  'lead(II)|chloride': { appearance: 'white', boundary: 'Lead(II) chloride redissolves in hot water.' },
  'lead(II)|bromide': { appearance: 'white' },
  'lead(II)|iodide': { appearance: 'bright yellow' },
  'lead(II)|sulfate': { appearance: 'white' },
  'lead(II)|carbonate': { appearance: 'white' },
  'lead(II)|hydroxide': { appearance: 'white', boundary: 'Lead(II) hydroxide dissolves again in excess sodium hydroxide.' },
  'lead(II)|phosphate': { appearance: 'white' },
  'barium|sulfate': { appearance: 'white' },
  'barium|carbonate': { appearance: 'white' },
  'barium|phosphate': { appearance: 'white' },
  'calcium|carbonate': { appearance: 'white' },
  'calcium|phosphate': { appearance: 'white' },
  'magnesium|hydroxide': { appearance: 'white' },
  'copper(II)|hydroxide': { appearance: 'pale blue' },
  'iron(II)|hydroxide': { appearance: 'dirty green', boundary: 'Iron(II) hydroxide slowly turns brown in air as it is oxidised.' },
  'iron(III)|hydroxide': { appearance: 'orange-brown' },
  'zinc|hydroxide': { appearance: 'white', boundary: 'Zinc hydroxide dissolves again in excess sodium hydroxide.' },
  'aluminium|hydroxide': { appearance: 'white', boundary: 'Aluminium hydroxide dissolves again in excess sodium hydroxide.' },
};

/** Insoluble combinations whose real product is not the simple salt, so they fail closed. */
export const EXCLUDED_PRECIPITATES: Record<string, string> = {
  'silver|hydroxide': 'Silver hydroxide is not stable: a brown precipitate of silver oxide, Ag₂O, forms instead.',
  'copper(II)|carbonate': 'Copper(II) ions and carbonate form a basic copper carbonate, not simple CuCO₃.',
  'iron(III)|carbonate': 'Iron(III) ions react with carbonate and water to give iron(III) hydroxide and carbon dioxide.',
  'iron(II)|carbonate': 'Iron(II) carbonate is quickly oxidised in air, so the product changes as you watch.',
  'magnesium|carbonate': 'Magnesium and carbonate ions form a basic magnesium carbonate whose formula varies.',
  'zinc|carbonate': 'Zinc and carbonate ions form a basic zinc carbonate whose formula varies.',
};

export interface ActivityEntry {
  symbol: string;
  name: string;
  ion: string;
}

/** Most reactive first. Hydrogen is included as the reference point for acids. */
export const ACTIVITY_SERIES: ActivityEntry[] = [
  { symbol: 'K', name: 'potassium', ion: 'potassium' },
  { symbol: 'Na', name: 'sodium', ion: 'sodium' },
  { symbol: 'Ca', name: 'calcium', ion: 'calcium' },
  { symbol: 'Mg', name: 'magnesium', ion: 'magnesium' },
  { symbol: 'Al', name: 'aluminium', ion: 'aluminium' },
  { symbol: 'Zn', name: 'zinc', ion: 'zinc' },
  { symbol: 'Fe', name: 'iron', ion: 'iron(II)' },
  { symbol: 'Sn', name: 'tin', ion: 'tin(II)' },
  { symbol: 'Pb', name: 'lead', ion: 'lead(II)' },
  { symbol: 'H', name: 'hydrogen', ion: 'hydrogen' },
  { symbol: 'Cu', name: 'copper', ion: 'copper(II)' },
  { symbol: 'Ag', name: 'silver', ion: 'silver' },
  { symbol: 'Au', name: 'gold', ion: 'gold(III)' },
];

export function activityRank(symbol: string) {
  return ACTIVITY_SERIES.findIndex((entry) => entry.symbol === symbol);
}

export const METAL_NOTES: Record<string, string> = {
  Al: 'Aluminium is protected by a thin, tough oxide layer, so its reaction can be slow to start.',
  Sn: 'Tin reacts only slowly with dilute acids; warming may be needed to see bubbles.',
  Mg: 'Magnesium may also release a little hydrogen, because many metal salt solutions are slightly acidic.',
};

export const SOLUTION_COLOURS: Record<string, string> = {
  'copper(II)': 'blue',
  'iron(II)': 'pale green',
  'iron(III)': 'yellow-brown',
};

export const METAL_DEPOSITS: Record<string, string> = {
  Cu: 'a pink-brown coating of copper',
  Ag: 'shiny grey crystals of silver',
  Pb: 'dark grey crystals of lead',
  Fe: 'a dark grey deposit of iron',
  Zn: 'a dark grey deposit of zinc',
  Sn: 'a grey deposit of tin',
};

export const TOO_REACTIVE_METALS = ['K', 'Na', 'Ca'];

// ---------------------------------------------------------------------------
// Reactant catalogues
// ---------------------------------------------------------------------------

export interface IonicReactant {
  id: string;
  cation: string;
  anion: string;
  observation?: string;
}

const ionic = (cation: string, anion: string, state: 'aq' | 's', observation?: string): IonicReactant => ({
  id: `${compoundFor(cation, anion).formula}(${state})`,
  cation,
  anion,
  observation,
});

export const PRECIPITATION_SOLUTIONS: IonicReactant[] = [
  ionic('silver', 'nitrate', 'aq'),
  ionic('lead(II)', 'nitrate', 'aq'),
  ionic('barium', 'chloride', 'aq'),
  ionic('calcium', 'chloride', 'aq'),
  ionic('magnesium', 'sulfate', 'aq'),
  ionic('copper(II)', 'sulfate', 'aq'),
  ionic('iron(II)', 'sulfate', 'aq'),
  ionic('iron(III)', 'chloride', 'aq'),
  ionic('zinc', 'sulfate', 'aq'),
  ionic('sodium', 'chloride', 'aq'),
  ionic('potassium', 'iodide', 'aq'),
  ionic('sodium', 'bromide', 'aq'),
  ionic('sodium', 'sulfate', 'aq'),
  ionic('sodium', 'carbonate', 'aq'),
  ionic('sodium', 'hydroxide', 'aq'),
  ionic('sodium', 'phosphate', 'aq'),
  ionic('potassium', 'nitrate', 'aq'),
];

export const DISPLACEMENT_SOLUTIONS: IonicReactant[] = [
  ionic('copper(II)', 'sulfate', 'aq'),
  ionic('zinc', 'sulfate', 'aq'),
  ionic('iron(II)', 'sulfate', 'aq'),
  ionic('magnesium', 'sulfate', 'aq'),
  ionic('silver', 'nitrate', 'aq'),
  ionic('lead(II)', 'nitrate', 'aq'),
];

export const DISPLACEMENT_METALS = ['Mg', 'Al', 'Zn', 'Fe', 'Pb', 'Cu', 'Ag'];
export const ACID_METALS = ['Mg', 'Al', 'Zn', 'Fe', 'Sn', 'Pb', 'Cu', 'Ag', 'Au'];

export interface AcidData {
  id: string;
  anion: string;
}

export const ACIDS: AcidData[] = [
  { id: 'HCl(aq)', anion: 'chloride' },
  { id: 'H2SO4(aq)', anion: 'sulfate' },
  { id: 'HNO3(aq)', anion: 'nitrate' },
];

export const DILUTE_ACIDS_FOR_METALS = ['HCl(aq)', 'H2SO4(aq)'];

export const CARBONATES: IonicReactant[] = [
  ionic('sodium', 'carbonate', 'aq'),
  ionic('potassium', 'carbonate', 'aq'),
  ionic('sodium', 'hydrogencarbonate', 'aq'),
  ionic('calcium', 'carbonate', 's', 'The white solid fizzes and gets smaller.'),
  ionic('magnesium', 'carbonate', 's', 'The white powder fizzes and disappears.'),
];

export const BASES: IonicReactant[] = [
  ionic('sodium', 'hydroxide', 'aq', 'No visible change, but the mixture warms. An indicator shows the pH moving towards 7.'),
  ionic('potassium', 'hydroxide', 'aq', 'No visible change, but the mixture warms. An indicator shows the pH moving towards 7.'),
  ionic('calcium', 'hydroxide', 'aq', 'No visible change, but the mixture warms slightly.'),
  ionic('magnesium', 'hydroxide', 's', 'The white solid dissolves as it is neutralised, as an antacid does in the stomach.'),
  ionic('magnesium', 'oxide', 's', 'The white powder dissolves as it is neutralised.'),
  ionic('copper(II)', 'oxide', 's', 'The black solid dissolves and the solution turns blue. Warming speeds this up.'),
];

export interface FuelData {
  id: string;
  name: string;
  /** Flame seen when the fuel burns in air; flame colour depends on the fuel and the oxygen supply. */
  observation: string;
}

const GAS_FUEL_FLAME =
  'With enough air premixed (Bunsen burner air hole open), it burns with a blue flame, releasing heat and light. Water condenses on a cold surface.';

export const FUELS: FuelData[] = [
  { id: 'CH4(g)', name: 'methane', observation: GAS_FUEL_FLAME },
  { id: 'C3H8(g)', name: 'propane', observation: GAS_FUEL_FLAME },
  { id: 'C4H10(g)', name: 'butane', observation: GAS_FUEL_FLAME },
  {
    id: 'C8H18(l)',
    name: 'octane',
    observation:
      'In open air, liquid octane burns with a yellow, smoky flame, because the air supply is not enough for complete combustion. Complete combustion needs a good oxygen supply, such as fuel vapour mixed with air in an engine.',
  },
  {
    id: 'CH3OH(l)',
    name: 'methanol',
    observation: 'Burns with a pale blue flame that is almost invisible in daylight. Water condenses on a cold surface.',
  },
  {
    id: 'C2H5OH(l)',
    name: 'ethanol',
    observation: 'Burns with a blue flame, releasing heat and light. Water condenses on a cold surface.',
  },
];

export interface CuratedCase {
  id: string;
  family: 'synthesis' | 'decomposition';
  reactants: EquationTerm[];
  products: EquationTerm[];
  outcome: ReactionOutcome;
  evidence: ObservableEvidence;
  observation: string;
  explanation: string;
  boundary?: string;
  safetyBoundary: string;
}

export const term = (coefficient: number, species: string): EquationTerm => ({ coefficient, species });

export const CURATED_CASES: CuratedCase[] = [
  {
    id: 'magnesium-oxygen',
    family: 'synthesis',
    reactants: [term(2, 'Mg(s)'), term(1, 'O2(g)')],
    products: [term(2, 'MgO(s)')],
    outcome: 'reaction',
    evidence: 'heat-light',
    observation: 'An intense white light, then a white powder of magnesium oxide.',
    explanation: 'Two elements combine into one compound: magnesium burns in oxygen to form magnesium oxide.',
    safetyBoundary: 'Do not look directly at burning magnesium; the light can damage eyes.',
  },
  {
    id: 'iron-sulfur',
    family: 'synthesis',
    reactants: [term(1, 'Fe(s)'), term(1, 'S(s)')],
    products: [term(1, 'FeS(s)')],
    outcome: 'reaction',
    evidence: 'heat-light',
    observation: 'Once heated, the mixture glows red and keeps glowing; a black solid forms.',
    explanation: 'Iron and sulfur combine into iron(II) sulfide, a compound with different properties from the mixture.',
    safetyBoundary: 'Heating sulfur can release toxic sulfur dioxide; this needs a fume cupboard and teacher control.',
  },
  {
    id: 'hydrogen-oxygen',
    family: 'synthesis',
    reactants: [term(2, 'H2(g)'), term(1, 'O2(g)')],
    products: [term(2, 'H2O(l)')],
    outcome: 'reaction',
    evidence: 'heat-light',
    observation: 'A lit splint gives a squeaky pop as hydrogen burns to water.',
    explanation: 'Hydrogen and oxygen combine into water and release energy.',
    safetyBoundary: 'Hydrogen–oxygen mixtures can explode. Use only teacher-prepared test-tube amounts.',
  },
  {
    id: 'carbon-oxygen',
    family: 'synthesis',
    reactants: [term(1, 'C(s)'), term(1, 'O2(g)')],
    products: [term(1, 'CO2(g)')],
    outcome: 'reaction',
    evidence: 'heat-light',
    observation: 'Hot carbon glows; a colourless gas forms that turns limewater milky.',
    explanation: 'Carbon combines with plenty of oxygen to form carbon dioxide.',
    boundary: 'With limited oxygen, toxic carbon monoxide forms instead. That case is not modelled.',
    safetyBoundary: 'Combustion requires supervised laboratory equipment.',
  },
  {
    id: 'calcium-carbonate-heat',
    family: 'decomposition',
    reactants: [term(1, 'CaCO3(s)')],
    products: [term(1, 'CaO(s)'), term(1, 'CO2(g)')],
    outcome: 'reaction',
    evidence: 'gas-co2',
    observation: 'The chip glows when heated strongly; the gas given off turns limewater milky.',
    explanation: 'A metal carbonate breaks down on strong heating into a metal oxide and carbon dioxide.',
    boundary: 'Calcium carbonate needs very strong heating (around 800 °C or more) to decompose fully.',
    safetyBoundary: 'Strong heating requires eye protection and teacher supervision.',
  },
  {
    id: 'copper-carbonate-heat',
    family: 'decomposition',
    reactants: [term(1, 'CuCO3(s)')],
    products: [term(1, 'CuO(s)'), term(1, 'CO2(g)')],
    outcome: 'reaction',
    evidence: 'gas-co2',
    observation: 'The green powder turns black; the gas turns limewater milky.',
    explanation: 'Copper(II) carbonate decomposes on heating into black copper(II) oxide and carbon dioxide.',
    boundary:
      'School “copper carbonate” is usually basic copper carbonate, CuCO₃·Cu(OH)₂, which also releases water. The simplified equation is shown.',
    safetyBoundary: 'Strong heating requires eye protection and teacher supervision.',
  },
  {
    id: 'zinc-carbonate-heat',
    family: 'decomposition',
    reactants: [term(1, 'ZnCO3(s)')],
    products: [term(1, 'ZnO(s)'), term(1, 'CO2(g)')],
    outcome: 'reaction',
    evidence: 'gas-co2',
    observation: 'The white solid turns yellow while hot and white again when cool; the gas turns limewater milky.',
    explanation: 'Zinc carbonate decomposes into zinc oxide and carbon dioxide. Zinc oxide is yellow only while hot.',
    safetyBoundary: 'Strong heating requires eye protection and teacher supervision.',
  },
  {
    id: 'magnesium-carbonate-heat',
    family: 'decomposition',
    reactants: [term(1, 'MgCO3(s)')],
    products: [term(1, 'MgO(s)'), term(1, 'CO2(g)')],
    outcome: 'reaction',
    evidence: 'gas-co2',
    observation: 'The white solid stays white; the gas given off turns limewater milky.',
    explanation: 'Magnesium carbonate decomposes on heating into magnesium oxide and carbon dioxide.',
    safetyBoundary: 'Strong heating requires eye protection and teacher supervision.',
  },
  {
    id: 'hydrogen-peroxide-catalyst',
    family: 'decomposition',
    reactants: [term(2, 'H2O2(aq)')],
    products: [term(2, 'H2O(l)'), term(1, 'O2(g)')],
    outcome: 'reaction',
    evidence: 'gas-o2',
    observation: 'With a pinch of manganese(IV) oxide catalyst, rapid fizzing; the gas relights a glowing splint.',
    explanation: 'Hydrogen peroxide breaks down into water and oxygen. The catalyst speeds this up without being used up.',
    boundary: 'This decomposition uses a catalyst rather than strong heating.',
    safetyBoundary: 'Hydrogen peroxide is corrosive at higher concentrations; use dilute solutions under supervision.',
  },
  {
    id: 'sodium-carbonate-heat',
    family: 'decomposition',
    reactants: [term(1, 'Na2CO3(s)')],
    products: [],
    outcome: 'no-reaction',
    evidence: 'no-change',
    observation: 'The white powder stays the same; limewater stays clear.',
    explanation:
      'Sodium carbonate does not decompose at Bunsen-burner temperatures. Group 1 carbonates (except lithium carbonate) are thermally stable.',
    safetyBoundary: 'Strong heating requires eye protection and teacher supervision.',
  },
];
